import React, { useEffect, useState } from 'react';
import { Building2, MapPin, Car, Users, Calculator, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface DashboardStats {
  cities: number;
  districts: number;
  neighborhoods: number;
  totalTaxiStands: number;
  verifiedStands: number;
  pendingStands: number;
  searchCount: number;
  fareCalculations: number;
  recentStands: any[];
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    cities: 0,
    districts: 0,
    neighborhoods: 0,
    totalTaxiStands: 0,
    verifiedStands: 0,
    pendingStands: 0,
    searchCount: 0,
    fareCalculations: 0,
    recentStands: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
    
    // Load counts from localStorage
    const savedSearchCount = parseInt(localStorage.getItem('totalSearchCount') || '0');
    const savedFareCalcs = parseInt(localStorage.getItem('totalFareCalculations') || '0');
    
    setStats(prev => ({
      ...prev,
      searchCount: savedSearchCount,
      fareCalculations: savedFareCalcs
    }));
  }, []);

  const loadDashboardStats = async () => {
    try {
      // Şehir sayısı
      const { count: citiesCount } = await supabase
        .from('cities')
        .select('*', { count: 'exact', head: true });

      // İlçe sayısı
      const { count: districtsCount } = await supabase
        .from('districts')
        .select('*', { count: 'exact', head: true });

      // Mahalle sayısı
      const { count: neighborhoodsCount } = await supabase
        .from('neighborhoods')
        .select('*', { count: 'exact', head: true });

      // Onaylı taksi durakları
      const { count: verifiedCount } = await supabase
        .from('taxi_stands')
        .select('*', { count: 'exact', head: true })
        .eq('is_verified', true);

      // Onay bekleyen taksi durakları
      const { count: pendingCount } = await supabase
        .from('taxi_stands')
        .select('*', { count: 'exact', head: true })
        .eq('is_verified', false);

      // Son eklenen taksi durakları
      const { data: recentStands } = await supabase
        .from('taxi_stands')
        .select(`
          id,
          name,
          address,
          phone,
          is_verified,
          created_at
        `)
        .order('created_at', { ascending: false })
        .limit(5);

      setStats({
        ...stats,
        cities: citiesCount || 0,
        districts: districtsCount || 0,
        neighborhoods: neighborhoodsCount || 0,
        totalTaxiStands: (verifiedCount || 0) + (pendingCount || 0),
        verifiedStands: verifiedCount || 0,
        pendingStands: pendingCount || 0,
        recentStands: recentStands || []
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-8 bg-gray-800 rounded-lg w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-40 bg-gray-800 rounded-xl"></div>
          ))}
        </div>
        <div className="h-96 bg-gray-800 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold text-white mb-8">Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Locations Stats */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Building2 className="w-8 h-8 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Toplam İl</p>
              <h3 className="text-2xl font-bold text-white">{stats.cities}</h3>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400">İlçeler</span>
              <span className="text-white font-medium">{stats.districts}</span>
            </div>
            <div className="flex justify-between items-center text-sm mt-2">
              <span className="text-gray-400">Mahalleler</span>
              <span className="text-white font-medium">{stats.neighborhoods}</span>
            </div>
          </div>
        </div>

        {/* Taxi Stats */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-yellow-400/10 rounded-lg">
              <Car className="w-8 h-8 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Toplam Taksi Durağı</p>
              <h3 className="text-2xl font-bold text-white">{stats.totalTaxiStands}</h3>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400">Onaylı Duraklar</span>
              <span className="text-white font-medium">{stats.verifiedStands}</span>
            </div>
            <div className="flex justify-between items-center text-sm mt-2">
              <span className="text-gray-400">Bekleyen Onaylar</span>
              <span className="text-white font-medium">{stats.pendingStands}</span>
            </div>
          </div>
        </div>

        {/* Usage Stats */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <Calculator className="w-8 h-8 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Toplam İşlem</p>
              <h3 className="text-2xl font-bold text-white">{stats.searchCount + stats.fareCalculations}</h3>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400">Taksi Aramaları</span>
              <span className="text-white font-medium">{stats.searchCount}</span>
            </div>
            <div className="flex justify-between items-center text-sm mt-2">
              <span className="text-gray-400">Ücret Hesaplamaları</span>
              <span className="text-white font-medium">{stats.fareCalculations}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-semibold text-white mb-6">Son Eklenen Taksi Durakları</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-400 text-sm">
                <th className="pb-4">Durak Adı</th>
                <th className="pb-4">Adres</th>
                <th className="pb-4">Telefon</th>
                <th className="pb-4">Durum</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {stats.recentStands.map((stand) => (
                <tr key={stand.id} className="border-t border-gray-700">
                  <td className="py-4">{stand.name}</td>
                  <td className="py-4">{stand.address}</td>
                  <td className="py-4">{stand.phone}</td>
                  <td className="py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      stand.is_verified 
                        ? 'bg-green-500/10 text-green-500' 
                        : 'bg-yellow-400/10 text-yellow-400'
                    }`}>
                      {stand.is_verified ? 'Onaylı' : 'Onay Bekliyor'}
                    </span>
                  </td>
                </tr>
              ))}
              {stats.recentStands.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-400">
                    Henüz taksi durağı eklenmemiş.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}